import { Injectable } from "@angular/core";
import { AuthService } from "app/auth/auth.service";
import {
  concat,
  filter,
  findKey,
  has,
  head,
  includes,
  isEmpty,
  isEqual,
  keys,
  map,
  mapKeys,
  partial,
  toLower,
  uniqBy,
  values,
} from "lodash";
import * as moment from "moment-timezone";
import { Observable } from "rxjs";
import { v4 as uuid } from "uuid";
import { OfflineAsmtDataServiceProvider } from "../offline-asmt-data-service/offline-asmt-data-service";
import { DatabaseProvider } from "../offline-database/db-connection";
import { DbQueries } from "../offline-database/db-queries";
import {
  ASSESSMENT_SECTION_TYPE_LOOKUP,
  ASSESSMENT_STATUS_LOOKUP,
  ASSESSMENT_TYPE_LOOKUP,
  DB_DATE_FORMAT,
  OFFLINE_ACTION_DATE_FORMAT,
  OFFLINE_ACTION_VALIDITY_IN_DAYS,
  OFFLINE_ASSESSMENT_ID_INITIAL,
  parseJSON,
  ROLE_LOOKUP,
  SECTION_CODE_PAIN,
  SECTION_CODE_SKIN,
  stringifyJSON,
  TEAM_ROLE_GROUP_ENUM,
} from "../offline-manager/offline-globals";
import { StateManagementService } from "../state-management/state-management.service";
import { AssessmentValid } from "./offline-asmt-data-interface";

@Injectable()
export class CreateAsmtService {
  msg: any;
  constructor(
    public database: DatabaseProvider,
    private authSvc: AuthService,
    private offlineAsmtData: OfflineAsmtDataServiceProvider,
    private stateData: StateManagementService
  ) {
    this.msg = { key: "disallow_current_user_role_visit_open" };
  }

  // CO-3454 (epic CO-3382): a null (never fetched) or absent flag keeps
  // today's exact blocking behavior - fail closed.
  private isConcurrentVisitsOn(): boolean {
    return !!this.stateData.concurrentVisitsToggle.getValue();
  }

  createVisit(payload): Observable<any> {
    return new Observable((observer) => {
      this.isNewAsmtValid(payload).subscribe(
        () => {
          this.getPatientProfile(payload.patChartId).subscribe((patient) => {
            this.createAssessment(payload, patient).subscribe((asmt) => {
              this.createAssessmentTemplate(asmt, payload, patient).subscribe(
                () => {
                  if (payload.roleLookupCode === "SD002.5") {
                    this.loadPreviousAsmtHAData(asmt).subscribe(
                      (previousAsmtHADetails: any) => {
                        previousAsmtHADetails.forEach((element) => {
                          this.prepareAndInsertAsmtDetails(element, asmt);
                        });
                        observer.next(asmt);
                      }
                    );
                  } else {
                    this.loadPreviousAssessmentQuesData(asmt).subscribe(
                      (previousAsmtDetails: any) => {
                        previousAsmtDetails.forEach((element) => {
                          this.prepareAndInsertAsmtDetails(element, asmt);
                        });
                        observer.next(asmt);
                      }
                    );
                  }
                }
              );
            });
          });
        },
        (error) => {
          observer.error(error);
        }
      );
    });
  }

  getPrevAsmtsByPatient(payload: any): Observable<any> {
    return new Observable((observer) => {
      if (payload.patChartId) {
        this.offlineAsmtData.getAssessments(payload.patChartId).subscribe(
          (asmt) => {
            observer.next(asmt.visits);
          },
          (error) => {
            observer.error(error);
          }
        );
      }
    });
  }

  loadPreviousAsmtHAData(asmt) {
    return new Observable((observer) => {
      this.getPreviousAsmtByRoleLookup(
        asmt.pat_chart_id,
        asmt.created_date,
        "SD002.3"
      ).subscribe((previousAsmtId) => {
        if (previousAsmtId) {
          this.offlineAsmtData
            .getCardTypeTemplate(asmt.id, "hospiceAideHACarePlan")
            .subscribe((template) => {
              this.getPreviousAsmtDetail(template, previousAsmtId).subscribe(
                (previousAsmtDetails) => {
                  let mappedPreviousAsmtDetails = [];

                  previousAsmtDetails.forEach((historyAssessmentDetail) => {
                    let previousDataJson = parseJSON(
                      historyAssessmentDetail.data_json
                    );
                    let carryThroughDataObj: any = {};
                    let carryThroughData: any = {};

                    template.cards.forEach((card) => {
                      if (
                        card.carryThrough &&
                        card.carryThroughCardLookupCode ===
                          historyAssessmentDetail.rec_type_lookup_code
                      ) {
                        if (card.cardName === "addTask") {
                          if (previousDataJson.task) {
                            carryThroughData.task = previousDataJson.task;
                          }
                          if (previousDataJson.category) {
                            carryThroughData.category =
                              previousDataJson.category;
                          }
                          if (previousDataJson.assistance) {
                            carryThroughData.assistance =
                              previousDataJson.assistance;
                          }
                          if (previousDataJson.frequency) {
                            carryThroughData.frequency =
                              previousDataJson.frequency;
                          }
                          carryThroughData.addedBy = asmt.performed_by;
                          carryThroughData.addedById = asmt.performed_by_id;
                          carryThroughData.date = moment().format(
                            OFFLINE_ACTION_DATE_FORMAT
                          );
                          carryThroughData.createDate = moment().format(
                            OFFLINE_ACTION_DATE_FORMAT
                          );
                        } else {
                          for (let key of keys(card.inputs)) {
                            carryThroughData[key] = previousDataJson[key];
                          }
                          carryThroughData.addedBy = previousDataJson.addedBy;
                          carryThroughData.addedById =
                            previousDataJson.addedById;
                          carryThroughData.date = previousDataJson.date;
                          carryThroughData.createDate =
                            previousDataJson.createDate;
                        }

                        carryThroughData.updateDate = moment().format(
                          OFFLINE_ACTION_DATE_FORMAT
                        );
                        carryThroughData.updatedBy = asmt.performed_by;
                        carryThroughData.updatedById = asmt.performed_by_id;
                        carryThroughData.id = uuid();

                        carryThroughDataObj.data_json =
                          stringifyJSON(carryThroughData);
                        carryThroughDataObj.section_type_lookup_code =
                          template.id;
                        carryThroughDataObj.rec_type_lookup_code = card.id;

                        mappedPreviousAsmtDetails.push(carryThroughDataObj);
                      }
                    });
                  });
                  observer.next(mappedPreviousAsmtDetails);
                }
              );
            });
          observer.next([]);
        } else {
          observer.next([]);
        }
      });
    });
  }

  loadPreviousAssessmentQuesData(asmt) {
    return new Observable((observer) => {
      this.getPreviousAsmtByRoleLookup(
        asmt.pat_chart_id,
        asmt.created_date,
        "SD002.3"
      ).subscribe((previousAsmtId) => {
        if (previousAsmtId) {
          this.offlineAsmtData
            .getCardTypeTemplate(asmt.id, "precautionsSafetyTeachings")
            .subscribe((template) => {
              this.getPreviousAsmtDetail(template, previousAsmtId).subscribe(
                (previousAsmtDetails) => {
                  this.offlineAsmtData
                    .getCardTypeTemplate(asmt.id, "vitals")
                    .subscribe((templateVitals) => {
                      this.getCarryThroughCardDetails(
                        templateVitals,
                        previousAsmtId
                      ).subscribe((historyAsmtDetails) => {
                        this.offlineAsmtData
                          .getCardTypeTemplate(asmt.id, "militaryHistory")
                          .subscribe((templateMilitaryHistory) => {
                            this.getCarryThroughCardDetailsForMilitary(
                              templateMilitaryHistory,
                              previousAsmtId
                            ).subscribe((militaryHistoryAsmtDetails) => {
                              this.offlineAsmtData
                                .getCardTypeTemplate(asmt.id, "hospiceAide")
                                .subscribe((templateHospiceAide) => {
                                  this.getCarryThroughHACardDetails(
                                    templateHospiceAide,
                                    previousAsmtId
                                  ).subscribe((hAasmtDetails) => {
                                    let previousAsmtData = concat(
                                      previousAsmtDetails,
                                      historyAsmtDetails,
                                      militaryHistoryAsmtDetails,
                                      hAasmtDetails
                                    );
                                    observer.next(previousAsmtData);
                                  });
                                });
                            });
                          });
                      });
                    });
                }
              );
            });
        } else {
          observer.next([]);
        }
      });
    });
  }

  private getCarryThroughCardDetailsForMilitary(
    template,
    previousAssessmentId
  ): Observable<any> {
    return new Observable((observer) => {
      this.database.getInstance().subscribe((db) => {
        db.transaction((tx) => {
          const cardIds = [];
          if (template.cards) {
            template.cards.forEach((card) => {
              cardIds.push(card.id);
            });
          }
          const query = `SELECT data_json, rec_type_lookup_code, section_type_lookup_code FROM assessment_details 
 				  WHERE assessment_id = ${previousAssessmentId} AND
 				  rec_type_lookup_code IN ('${cardIds.join("','")}')`;
          tx.executeSql(
            query,
            [],
            (tx, historyAssessmentDetails) => {
              const historyAsmtDetail: any = [];

              for (let x = 0; x < historyAssessmentDetails.rows.length; x++) {
                let historyAsmt = historyAssessmentDetails.rows.item(x);
                historyAsmt.data_json = parseJSON(historyAsmt.data_json);
                historyAsmt.data_json.history = historyAsmt.data_json.current;
                historyAsmt.data_json = stringifyJSON(historyAsmt.data_json);
                historyAsmtDetail.push(historyAsmt);
              }
              observer.next(historyAsmtDetail);
            },
            (_, error) => {
              console.log(
                `GET getCarryThroughCardDetails ERROR: ${error.message}`
              );
              observer.error(error);
            },
            () => {
              observer.complete();
            }
          );
        });
      });
    });
  }

  private getCarryThroughHACardDetails(
    template,
    previousAssessmentId
  ): Observable<any> {
    return new Observable((observer) => {
      this.database.getInstance().subscribe((db) => {
        db.transaction((tx) => {
          const cardIds = [];
          if (template.cards) {
            template.cards.forEach((card) => {
              if (card.cardName !== "notes" && card.cardName !== "decline") {
                cardIds.push(card.id);
              }
            });
          }
          const query = `SELECT data_json, rec_type_lookup_code, section_type_lookup_code FROM assessment_details 
				  						WHERE assessment_id = ${previousAssessmentId} AND
										  rec_type_lookup_code IN ('${cardIds.join("','")}')`;
          tx.executeSql(
            query,
            [],
            (tx, previousAssessmentDetails) => {
              const previousAsmtDetail: any = [];
              for (let x = 0; x < previousAssessmentDetails.rows.length; x++) {
                previousAsmtDetail.push(previousAssessmentDetails.rows.item(x));
              }
              observer.next(previousAsmtDetail);
            },
            (_, error) => {
              console.log(
                `GET getCarryThroughCardDetails ERROR: ${error.message}`
              );
              observer.error(error);
            },
            () => {
              observer.complete();
            }
          );
        });
      });
    });
  }

  private getCarryThroughCardDetails(
    template,
    previousAssessmentId
  ): Observable<any> {
    return new Observable((observer) => {
      this.database.getInstance().subscribe((db) => {
        db.transaction((tx) => {
          const cardIds = [];
          if (template.cards) {
            template.cards.forEach((card) => {
              if (
                has(card, "inputs") &&
                has(card.inputs, "current") &&
                has(card.inputs.current, "carryThrough") &&
                card.inputs.current.carryThrough === true
              ) {
                cardIds.push(card.id);
              }
            });
          }
          const query = `SELECT data_json, rec_type_lookup_code, section_type_lookup_code FROM assessment_details 
				  WHERE assessment_id = ${previousAssessmentId} AND
				  rec_type_lookup_code IN ('${cardIds.join("','")}')`;
          tx.executeSql(
            query,
            [],
            (tx, historyAssessmentDetails) => {
              const historyAsmtDetail: any = [];
              for (let x = 0; x < historyAssessmentDetails.rows.length; x++) {
                let historyAsmt = historyAssessmentDetails.rows.item(x);
                historyAsmt.data_json = parseJSON(historyAsmt.data_json);
                historyAsmt.data_json.history = historyAsmt.data_json.current;
                historyAsmt.data_json = stringifyJSON(historyAsmt.data_json);
                historyAsmtDetail.push(historyAsmt);
              }
              observer.next(historyAsmtDetail);
            },
            (_, error) => {
              console.log(
                `GET getCarryThroughCardDetails ERROR: ${error.message}`
              );
              observer.error(error);
            },
            () => {
              observer.complete();
            }
          );
        });
      });
    });
  }

  getPreviousAsmtByRoleLookup(
    patientId,
    createdDate,
    roleLookupCode
  ): Observable<any> {
    return new Observable((observer) => {
      this.database.getInstance().subscribe((db) => {
        db.transaction((tx) => {
          tx.executeSql(
            DbQueries.SELECT_PREVIOUS_ASSESSMENT_ID_BY_ROLE_LOOKUP,
            [patientId, createdDate, roleLookupCode],
            (_, previousAsmtIdData) => {
              let previousAsmtId: any;
              if (previousAsmtIdData.rows.length > 0) {
                previousAsmtId = previousAsmtIdData.rows.item(0).asmt_id;
              }
              observer.next(previousAsmtId);
            },
            (_, error) => {
              console.log(
                `GET PREVIOUS ASSESSMENT ID BY ROLE LOOKUP error: ${error.message}`
              );
              observer.error(error);
            },
            () => {
              observer.complete();
            }
          );
        });
      });
    });
  }

  private prepareAndInsertAsmtDetails(quesData, asmt): any {
    this.database.getInstance().subscribe(
      (db) => {
        db.transaction(
          (tx) => {
            const { id, pat_chart_id, performed_by_id } = asmt;
            const createdDate = moment().format("MMMM Do, YYYY h:mm:ss a");
            const uuid = parseJSON(quesData.data_json).id;
            tx.executeSql(
              DbQueries.INSERT_ASSESSMENT_DETAILS_AFTER_EDIT,
              [
                uuid,
                pat_chart_id,
                id,
                quesData.section_type_lookup_code,
                quesData.rec_type_lookup_code,
                quesData.data_json,
                createdDate,
                createdDate,
                performed_by_id,
                performed_by_id,
                true,
              ],
              (tx, rs) => {},
              (tx, error) => {
                console.log(
                  `INSERT ERROR (prepareAndInsertAsmtDetails): ${error.message}`
                );
              }
            );
          },
          (error) => {
            console.log(
              `prepareAndInsertAsmtDetails transaction error: ${error.message}`
            );
          },
          () => {
            console.log(
              `prepareAndInsertAsmtDetails transaction completed at ${new Date()}`
            );
          }
        );
      },
      (error) => {
        console.log(
          `prepareAndInsertAsmtDetails.getInstance error: ${error.message}`
        );
      }
    );
  }

  getPreviousAsmtDetail(template, previousAssessmentId): Observable<any> {
    return new Observable((observer) => {
      this.database.getInstance().subscribe((db) => {
        db.transaction((tx) => {
          const cardIds = [];
          if (template.cards) {
            template.cards.forEach((card) => {
              if (template.sectionName === "hospiceAideHACarePlan") {
                if (card.cardName !== "notes" && card.cardName !== "decline") {
                  cardIds.push(card.carryThroughCardLookupCode);
                }
              } else {
                if (
                  card.cardName !== "notes" &&
                  card.type === "questionsCard"
                ) {
                  cardIds.push(card.id);
                }
              }
            });
          }
          const query = `SELECT data_json, rec_type_lookup_code, section_type_lookup_code FROM assessment_details 
				  WHERE assessment_id = ${previousAssessmentId} AND
				  rec_type_lookup_code IN ('${cardIds.join("','")}')`;
          tx.executeSql(
            query,
            [],
            (tx, previousAssessmentDetails) => {
              const previousAsmtDetail: any = [];
              for (let x = 0; x < previousAssessmentDetails.rows.length; x++) {
                previousAsmtDetail.push(previousAssessmentDetails.rows.item(x));
              }
              observer.next(previousAsmtDetail);
            },
            (_, error) => {
              console.log(
                `GET PREVIOUS ASSESSMENT DETAIL create assessment ERROR: ${error.message}`
              );
              observer.error(error);
            },
            () => {
              observer.complete();
            }
          );
        });
      });
    });
  }

  isNewAsmtValid(payload) {
    return new Observable((observer) => {
      const { roleLookupCode, patChartId, asmtTypeLookupCode } = payload;
      const teamRole = ROLE_LOOKUP[`${roleLookupCode}`];
      const concurrentVisitsOn = this.isConcurrentVisitsOn();
      this.fetchAsmtValidation(payload, concurrentVisitsOn).subscribe(
        (data: any) => {
          if (data === null) {
            observer.next(true);
            return;
          }
          const isAllowed = this.isUserAllowToCreateAsmt(
            data,
            asmtTypeLookupCode,
            TEAM_ROLE_GROUP_ENUM[`${teamRole}`],
            concurrentVisitsOn
          );
          if (concurrentVisitsOn) {
            // CO-3454 (epic CO-3382): with concurrent visits on, a visit
            // already created offline for this patient no longer blocks a
            // new start, so the offline-created check is skipped; only the
            // non-concurrency validations above still refuse.
            if (isAllowed) {
              observer.next(isAllowed);
            } else {
              observer.error({ errorMessage: this.msg, status: 500 });
            }
            return;
          }
          this.isAsmtCreatedInOfflineMode(payload).subscribe(
            (result) => {
              if (isAllowed) {
                observer.next(isAllowed);
              } else {
                observer.error({ errorMessage: this.msg, status: 500 });
              }
            },
            (_) => {
              observer.error({ errorMessage: this.msg, status: 500 });
            }
          );
        },
        (_) => {
          observer.error({ errorMessage: this.msg, status: 500 });
        }
      );
    });
  }

  isUserAllowToCreateAsmt(
    data,
    asmtTypeLookupCode,
    teamRole,
    concurrentVisitsOn = false
  ) {
    const { admitVisit, previousAsmt, icaVisit, openAsmt } = data;
    // CO-3454 (epic CO-3382): an open visit only blocks while the
    // concurrent_visits flag is off; the remaining validations are not
    // concurrency rules and always apply.
    if (!concurrentVisitsOn && openAsmt > 0) {
      return false;
    }
    if (admitVisit > 0) {
      return false;
    }
    if (!isEmpty(previousAsmt)) {
      if (includes(previousAsmt[`${asmtTypeLookupCode}`], teamRole)) {
        return false;
      }
    }
    if (!isEmpty(icaVisit) && asmtTypeLookupCode === "CL003.1") {
      if (icaVisit[`${teamRole}`]) {
        return false;
      }
    }
    return true;
  }

  fetchAsmtValidation(payload, concurrentVisitsOn = false) {
    return new Observable((observer) => {
      this.database.getInstance().subscribe((db) => {
        db.transaction((tx) => {
          const { patChartId } = payload;
          tx.executeSql(
            DbQueries.FETCH_ASMT_VALIDATION_BY_PATIENT,
            [patChartId],
            (tx, rs) => {
              if (rs.rows.length > 0) {
                const validation = this.prepareAsmtValidation(rs);
                observer.next(validation);
              } else {
                tx.executeSql(
                  DbQueries.IS_ASMT_EXISTS_FOR_PATIENT,
                  [patChartId],
                  (tx, rs) => {
                    const data = rs.rows.item(0);
                    if (rs.rows.length > 0 && data.count > 0) {
                      // CO-3454 (epic CO-3382): a patient with no validation
                      // row but existing local visits only blocks while the
                      // concurrent_visits flag is off.
                      if (concurrentVisitsOn) {
                        observer.next(null);
                      } else {
                        observer.error();
                      }
                    } else {
                      observer.next(null);
                    }
                  }
                );
              }
            },
            (_, error) => {
              console.log("fetchAsmtValidation error: ", error.message);
              observer.error(error);
            },
            () => {
              observer.complete();
              console.log("fetchAsmtValidation Transaction Ok: ");
            }
          );
        });
      });
    });
  }

  prepareAsmtValidation(resultSet) {
    return {
      openAsmt: resultSet.rows.item(0).open_asmt,
      previousAsmt: parseJSON(resultSet.rows.item(0).previouse_asmt),
      icaVisit: parseJSON(resultSet.rows.item(0).ica_visit),
      admitVisit: resultSet.rows.item(0).admit_visit,
    };
  }

  isAsmtCreatedInOfflineMode(payload) {
    return new Observable((observer) => {
      this.database.getInstance().subscribe((db) => {
        db.transaction((tx) => {
          const { patChartId } = payload;
          tx.executeSql(
            DbQueries.IS_ASMT_CREATED_IN_OFFLINE_MODE,
            [patChartId],
            (tx, rs) => {
              const data = rs.rows.item(0);
              if (rs.rows.length > 0 && data.count > 0) {
                observer.error();
              } else {
                observer.next(null);
              }
            },
            (_, error) => {
              console.log("isAsmtCreatedInOfflineMode error: ", error.message);
              observer.error(error);
            },
            () => {
              observer.complete();
              console.log("isAsmtCreatedInOfflineMode Transaction Ok: ");
            }
          );
        });
      });
    });
  }

  createAssessment(payload: AssessmentValid, patient: any) {
    return new Observable((observer) => {
      this.getLatestAssessmentId().subscribe((asmtId) => {
        const authUser = this.authSvc.getUser();
        // const authUserRole = parseJSON(authUser.profile.role)[0];
        const user = {
          userId: authUser.userId,
          // userRole: authUserRole.groups ? authUserRole.groups[0].name : '',
          userRole: "", //NEED TO CHECK FOR THE ROLE
          userName: authUser.name,
        };
        const offlineAsmtDetails = this.prepareOfflineAsmtDetails(
          payload,
          asmtId,
          user
        );
        this.insertOfflineAsmtDetails(offlineAsmtDetails).subscribe(() => {
          this.insertOfflineActionAfterCreate(offlineAsmtDetails, patient);
          observer.next(offlineAsmtDetails);
        });
      });
    });
  }

  createAssessmentTemplate(asmt, payload, patient) {
    return new Observable((observer) => {
      const { asmtTypeLookupCode, roleLookupCode } = payload;
      this.fetchAsmtTmplConfigByLookupCode(asmtTypeLookupCode).subscribe(
        (tmplJson) => {
          this.fetchPermissionByRole(roleLookupCode).subscribe((perms) => {
            const filteredTmplConfig = this.filterByPermission(tmplJson, perms);
            const asmtTmplConfig = mapKeys(filteredTmplConfig, (v, _) => v._id);

            this.fetchSectionTypeLookupCode(asmtTmplConfig).subscribe(
              (sectionTypeLookupCodes) => {
                this.fetchAsmtSectionTmplConfig(
                  sectionTypeLookupCodes,
                  patient
                ).subscribe((sectionTmplConfig: any) => {
                  this.createNavigationTemplate(filteredTmplConfig).subscribe(
                    (navTmpl) => {
                      // Append navigation tmpl in section tmpl for saving in assessment_template
                      sectionTmplConfig.push(navTmpl);

                      this.database.getInstance().subscribe((db) => {
                        db.transaction((tx) => {
                          tx.executeSql(
                            DbQueries.SELECT_ASSESSMENT_TEMPLATE_MAX_ID,
                            [],
                            (tx, rs) => {
                              const maxId = Number(
                                rs.rows.item(0).asmt_tmpl_id
                              );
                              const asmtTmplId =
                                maxId >= OFFLINE_ASSESSMENT_ID_INITIAL
                                  ? maxId + 1
                                  : OFFLINE_ASSESSMENT_ID_INITIAL;
                              const sectionTmpl = this.prepareSectionTmpl(
                                asmt,
                                asmtTmplId,
                                sectionTmplConfig
                              );
                              const data = this.insertAssessmentTemplate(
                                sectionTmpl,
                                tx
                              );
                              observer.next(data);
                            },
                            (error) => {
                              console.log(
                                "createAssessmentTemplate Transaction ERROR: ",
                                error.message
                              );
                              observer.error(error);
                            },
                            () => {
                              console.log(
                                "createAssessmentTemplate Transaction OK"
                              );
                              observer.complete();
                            }
                          );
                        });
                      });
                    }
                  );
                });
              }
            );
          });
        }
      );
    });
  }

  filterByPermission(tmplJson, perms) {
    const parsedTmpl = parseJSON(tmplJson.value);
    const allowedNavs = map(perms, "access_lookup_code");
    const filteredPerm = filter(parsedTmpl.assessment_sections, (config) => {
      if (config._id === "levelOfCare") {
        return false;
      }
      return includes(allowedNavs, `/${toLower(config._id)}`);
    });
    return filteredPerm;
  }

  prepareSectionTmpl(asmt, asmtTmplId, sectionTmplJson) {
    const sectionTmpls = [];
    let tmplId = asmtTmplId;
    const user = this.authSvc.getUser();

    map(sectionTmplJson, (data) => {
      if (data.id !== null) {
        if (
          !(
            asmt.asmt_type_lookup_code === "CL003.12" &&
            data.section_type_lookup_code === "CL001.6" &&
            !data.tmpl_json.includes("activePain") &&
            !data.tmpl_json.includes("painScreeningTool")
          )
        ) {
          sectionTmpls.push({
            id: tmplId,
            uuid: uuid(),
            // 'tenant_id': parseJSON(user.profile.role)[0].orgID,
            tenant_id: user.tenantId,
            pat_chart_id: asmt.pat_chart_id,
            assessment_id: asmt.id,
            rec_type_lookup_code:
              ASSESSMENT_SECTION_TYPE_LOOKUP[
                `${data.section_type_lookup_code}`
              ],
            tmpl_json: data.tmpl_json,
            created_by: asmt.performed_by,
            created_date: moment().format(DB_DATE_FORMAT),
            updated_by: null,
            updated_date: null,
            version: 1,
            active_flag: true,
          });
          tmplId++;
        }
      }
    });
    return sectionTmpls;
  }

  insertAssessmentTemplate(sectionTmpl, tx: any): any {
    const uniqTmpl = uniqBy(sectionTmpl, "rec_type_lookup_code");
    map(uniqTmpl, (tmplData) => {
      const {
        id,
        uuid,
        tenant_id,
        pat_chart_id,
        assessment_id,
        rec_type_lookup_code,
        tmpl_json,
        created_by,
        created_date,
        updated_by,
        updated_date,
        version,
        active_flag,
      } = tmplData;
      if (id !== null) {
        tx.executeSql(DbQueries.ASSESSMENT_TEMPLATE_INSERT, [
          id,
          uuid,
          tenant_id,
          pat_chart_id,
          assessment_id,
          rec_type_lookup_code,
          tmpl_json,
          created_by,
          created_date,
          updated_by,
          updated_date,
          version,
          active_flag,
        ]);
      }
    });
  }

  getLatestAssessmentId() {
    return new Observable((observer) => {
      this.database.getInstance().subscribe((db) => {
        db.transaction((tx) => {
          tx.executeSql(
            DbQueries.SELECT_ASSESSMENT_MAX_ID,
            [],
            (tx, rs) => {
              if (rs.rows.length > 0) {
                const maxId = Number(rs.rows.item(0).assessment_id);
                const asmtId =
                  maxId >= OFFLINE_ASSESSMENT_ID_INITIAL
                    ? maxId + 1
                    : OFFLINE_ASSESSMENT_ID_INITIAL;
                observer.next(asmtId);
              }
            },
            (error) => {
              console.log(
                "getLatestAssessmentId Transaction ERROR: ",
                error.message
              );
              observer.error(error);
            },
            () => {
              console.log("getLatestAssessmentId Transaction OK");
              observer.complete();
            }
          );
        });
      });
    });
  }

  prepareOfflineAsmtDetails(payload, asmtId, user) {
    const { asmtTypeLookupCode, patChartId, roleLookupCode } = payload;
    return {
      id: asmtId,
      uuid: uuid(),
      pat_chart_id: patChartId,
      addendum: false,
      asmt_status_lookup_code: head(keys(ASSESSMENT_STATUS_LOOKUP)),
      asmt_status_lookup_value: head(values(ASSESSMENT_STATUS_LOOKUP)),
      asmt_type_lookup_code: asmtTypeLookupCode,
      asmt_type_lookup_value: ASSESSMENT_TYPE_LOOKUP[`${asmtTypeLookupCode}`],
      care_plan_status_lookup_code: head(keys(ASSESSMENT_STATUS_LOOKUP)),
      care_plan_status_lookup_value: head(values(ASSESSMENT_STATUS_LOOKUP)),
      coordinationOfCareAddendum: false,
      duration: null,
      end_dte: null,
      performed_by: user.userName,
      performed_by_id: user.userId,
      role_lookup_code: roleLookupCode,
      role_lookup_value: ROLE_LOOKUP[`${roleLookupCode}`],
      started_dte: null,
      timezone: moment.tz.guess(),
      created_date: moment().valueOf(),
      completed_date: null,
      offline_insert: 1,
    };
  }

  insertOfflineAsmtDetails(offlineAsmtDetails) {
    return new Observable((observer) => {
      this.database.getInstance().subscribe((db) => {
        db.transaction((tx) => {
          const {
            id,
            uuid,
            pat_chart_id,
            addendum,
            asmt_status_lookup_code,
            asmt_status_lookup_value,
            asmt_type_lookup_code,
            asmt_type_lookup_value,
            care_plan_status_lookup_code,
            care_plan_status_lookup_value,
            coordinationOfCareAddendum,
            duration,
            end_dte,
            performed_by,
            performed_by_id,
            role_lookup_code,
            role_lookup_value,
            started_dte,
            timezone,
            created_date,
            completed_date,
            offline_insert,
          } = offlineAsmtDetails;

          tx.executeSql(
            DbQueries.ASSESSMENT_INSERT,
            [
              id,
              uuid,
              pat_chart_id,
              addendum,
              asmt_status_lookup_code,
              asmt_status_lookup_value,
              asmt_type_lookup_code,
              asmt_type_lookup_value,
              care_plan_status_lookup_code,
              care_plan_status_lookup_value,
              coordinationOfCareAddendum,
              duration,
              end_dte,
              performed_by,
              performed_by_id,
              role_lookup_code,
              role_lookup_value,
              started_dte,
              timezone,
              created_date,
              completed_date,
              offline_insert,
            ],
            (tx, rs) => {
              observer.next();
            },
            (error) => {
              console.log(
                "insertOfflineAsmtDetails Transaction ERROR: ",
                error.message
              );
              observer.error(error);
            },
            () => {
              console.log("insertOfflineAsmtDetails Transaction OK");
              observer.complete();
            }
          );
        });
      });
    });
  }

  public getPatientProfile(chartId: number): Observable<any> {
    return new Observable((observer) => {
      this.database.getInstance().subscribe((db) => {
        db.transaction(
          (tx) => {
            tx.executeSql(
              DbQueries.PROFILE_SELECT_BY_PATIENT,
              [chartId],
              (tx, rs) => {
                let data = null;
                if (rs.rows.length > 0) {
                  data = rs.rows.item(0);
                }
                observer.next(data);
              }
            );
          },
          (error) => {
            console.log("getPatientProfile Transaction ERROR: ", error.message);
            observer.error(error);
          },
          () => {
            console.log("getPatientProfile Transaction OK");
            observer.complete();
          }
        );
      });
    });
  }

  public insertOfflineActionAfterCreate(offlineAsmtDetails: any, patient: any) {
    const {
      id,
      pat_chart_id,
      asmt_type_lookup_code,
      asmt_type_lookup_value,
      role_lookup_code,
    } = offlineAsmtDetails;

    this.database.getInstance().subscribe((db) => {
      db.transaction(
        (tx) => {
          const patient_name = patient ? patient.patientFullName : "";
          const updated_at = moment().valueOf();
          const expire_at = moment()
            .add(OFFLINE_ACTION_VALIDITY_IN_DAYS, "days")
            .startOf("day")
            .valueOf();

          // Insert offline action in DB
          tx.executeSql(
            DbQueries.OFFLINE_ACTION_INSERT,
            [
              id,
              "CREATE",
              asmt_type_lookup_value,
              null,
              pat_chart_id,
              patient_name,
              expire_at,
              updated_at,
              asmt_type_lookup_code,
              role_lookup_code,
            ],
            (tx, rs) => {},
            (tx, error) => {
              console.log(
                `INSERT ERROR (insertOfflineActionAfterCreate): ${error.message}`
              );
            }
          );
        },
        (error) => {
          console.log(
            `Transaction ERROR (insertOfflineActionAfterCreate, Assessment: ${id}: ${error.message}`
          );
        },
        () => {
          console.log(
            `Transaction ok (insertOfflineActionAfterCreate, Assessment: ${id}`
          );
        }
      );
    });
  }

  fetchAsmtTmplConfigByLookupCode(asmtTypeLookupCode: any) {
    return new Observable((observer) => {
      this.database.getInstance().subscribe((db) => {
        db.transaction((tx) => {
          tx.executeSql(
            DbQueries.SELECT_ASMT_TEMPLATE_CONFIG_BY_ASMT_TYPE_LOOKUP_CODE,
            [asmtTypeLookupCode],
            (tx, rs) => {
              if (rs.rows.length > 0) {
                const tmplJson = parseJSON(rs.rows.item(0).tmpl_json);
                observer.next(tmplJson);
              }
            },
            (_, error) => {
              console.log(
                "fetchAsmtTmplConfigByLookupCode error: " + error.message
              );
              observer.error(error);
            },
            () => {
              observer.complete();
            }
          );
        });
      });
    });
  }

  createNavigationTemplate(parsedTmpl): Observable<any> {
    return new Observable((observer) => {
      const asmtNavTmpl = this.prepareNavigationDetails(parsedTmpl);
      const asmtNavigation = {
        section_type_lookup_code: "navigation",
        tmpl_json: stringifyJSON({
          type: "jsonb",
          value: stringifyJSON(asmtNavTmpl),
        }),
      };
      observer.next(asmtNavigation);
    });
  }

  prepareNavigationDetails(parsedTmpl) {
    const navigations = {
      name: null,
      title: parsedTmpl.title,
      assessmentSections: [],
    };

    map(parsedTmpl, (data) => {
      navigations.assessmentSections.push({
        name: data["_id"],
        access: "write",
        sectionId: findKey(
          ASSESSMENT_SECTION_TYPE_LOOKUP,
          partial(isEqual, data["_id"])
        ),
        resourceId: "0",
        sort_order: data.sort_order,
      });
    });
    return navigations;
  }

  fetchSectionTypeLookupCode(asmtTmplConfig) {
    return new Observable((observer) => {
      this.database.getInstance().subscribe((db) => {
        db.transaction(
          (tx) => {
            const secTypeLookupValue = `'${keys(asmtTmplConfig).join("','")}'`;
            const query = `SELECT section_type_lookup_code FROM assessment_section_type_enum
							WHERE section_type_lookup_value IN (${secTypeLookupValue})`;
            tx.executeSql(
              query,
              [],
              (tx, rs) => {
                const lookupEnumCount = rs.rows.length;
                const secValues = [];
                if (lookupEnumCount > 0) {
                  for (let i = 0; i < lookupEnumCount; i++) {
                    secValues.push(rs.rows.item(i).section_type_lookup_code);
                  }
                }
                observer.next(secValues);
              },
              (_, error) => {
                console.error(
                  "fetchSectionTypeLookupCode error: " + error.message
                );
              }
            );
          },
          (err) => {
            console.error("fetchSectionTypeLookupCode transation error", err);
            observer.error(err);
          },
          (error) => {
            console.log("fetchSectionTypeLookupCode.getInstance() ok", error);
            observer.complete();
          }
        );
      });
    });
  }

  fetchAsmtSectionTmplConfig(sectionTypeLookupCodes, patient) {
    return new Observable((observer) => {
      this.database.getInstance().subscribe((db) => {
        db.transaction(
          (tx) => {
            const secTypeLookupCode = `'${sectionTypeLookupCodes.join("','")}'`;
            const query = `SELECT tmpl_json, section_type_lookup_code FROM assessment_section_template_config
							WHERE section_type_lookup_code IN (${secTypeLookupCode})`;
            tx.executeSql(
              query,
              [],
              (tx, rs) => {
                const lookupCount = rs.rows.length;
                const sectionTmpl = [];
                if (lookupCount > 0) {
                  for (let i = 0; i < lookupCount; i++) {
                    let { section_type_lookup_code, tmpl_json } =
                      rs.rows.item(i);
                    tmpl_json = this.setWoundCardBackground(
                      section_type_lookup_code,
                      tmpl_json,
                      patient ? patient.patientGender : "M"
                    );
                    sectionTmpl.push({
                      section_type_lookup_code,
                      tmpl_json,
                    });
                  }
                  observer.next(sectionTmpl);
                }
              },
              (_, error) => {
                console.error(
                  "fetchAsmtSectionTmplConfig error: " + error.message
                );
              }
            );
          },
          (err) => {
            console.error("fetchAsmtSectionTmplConfig transation error", err);
            observer.error(err);
          },
          (error) => {
            console.log("fetchAsmtSectionTmplConfig.getInstance() ok", error);
            observer.complete();
          }
        );
      });
    });
  }

  setWoundCardBackground(
    sectionCode: string,
    template: string,
    gender: string = "M"
  ): string {
    try {
      if (
        (SECTION_CODE_SKIN === sectionCode ||
          SECTION_CODE_PAIN === sectionCode) &&
        gender === "F" &&
        template.indexOf("pain/male.svg") > -1
      ) {
        template = template.replace(/pain\/male.svg/g, "pain/female.svg");
      }
    } catch (e) {
      console.log("Failed to setWoundCardBackground");
    }
    return template;
  }

  fetchPermissionByRole(roleLookupCode: any) {
    return new Observable((observer) => {
      this.database.getInstance().subscribe((db) => {
        db.transaction((tx) => {
          tx.executeSql(
            DbQueries.FETCH_PERMISSION_BY_ROLE,
            [roleLookupCode],
            (tx, rs) => {
              const permCount = rs.rows.length;
              const permMap = [];
              if (permCount > 0) {
                for (let i = 0; i < permCount; i++) {
                  permMap.push(rs.rows.item(i));
                }
                observer.next(permMap);
              }
            },
            (_, error) => {
              console.log("fetchPermissionByRole error: " + error.message);
              observer.error(error);
            },
            () => {
              observer.complete();
            }
          );
        });
      });
    });
  }
}



// WEBPACK FOOTER //
// ./src/providers/offline-asmt-data-service/create-asmt-service.ts